// Import students and classes to generate comprehensive test results
import { STUDENTS } from './students';
import { CLASSES } from './classes';

// Test templates for each subject
const testTemplates = {
  'Mathematics': [
    {
      name: 'Algebra Fundamentals',
      questions: [
        { text: 'Solve: 2x + 5 = 15', topic: 'Linear Equations', difficulty: 'Easy', points: 10 },
        { text: 'Expand: (x + 3)(x - 2)', topic: 'Polynomials', difficulty: 'Medium', points: 15 },
        { text: 'Factorize: x² - 9', topic: 'Factoring', difficulty: 'Medium', points: 15 },
        { text: 'Solve quadratic: x² - 5x + 6 = 0', topic: 'Quadratics', difficulty: 'Hard', points: 20 },
      ]
    },
    {
      name: 'Functions & Graphs',
      questions: [
        { text: 'Find f(3) if f(x) = 2x + 1', topic: 'Functions', difficulty: 'Easy', points: 10 },
        { text: 'Graph y = 2x - 3', topic: 'Linear Graphs', difficulty: 'Medium', points: 15 },
        { text: 'Find slope between (2,3) and (5,9)', topic: 'Slope', difficulty: 'Easy', points: 10 },
        { text: 'Determine if function is even/odd', topic: 'Function Properties', difficulty: 'Hard', points: 20 },
      ]
    },
    {
      name: 'Calculus Basics',
      questions: [
        { text: 'Find derivative of f(x) = x²', topic: 'Differentiation', difficulty: 'Medium', points: 15 },
        { text: 'Evaluate limit as x→2 of (x²-4)/(x-2)', topic: 'Limits', difficulty: 'Hard', points: 20 },
        { text: 'Find slope of tangent at x=3', topic: 'Applications', difficulty: 'Hard', points: 20 },
        { text: 'What is derivative of constant?', topic: 'Rules', difficulty: 'Easy', points: 10 },
      ]
    }
  ],
  'Physics': [
    {
      name: 'Forces & Motion Test',
      questions: [
        { text: 'Calculate net force with F1=10N and F2=5N opposite', topic: 'Force Vectors', difficulty: 'Easy', points: 10 },
        { text: 'Derive a from F=ma, m=5kg, F=25N', topic: 'Newton 2nd Law', difficulty: 'Medium', points: 15 },
        { text: 'Explain mass vs acceleration relationship', topic: 'Conceptual', difficulty: 'Easy', points: 10 },
        { text: 'Car: 0→60m/s in 10s. Find a,F if m=1200kg', topic: 'Kinematics', difficulty: 'Hard', points: 20 },
        { text: 'Draw free body diagram: block on 30° plane', topic: 'Diagrams', difficulty: 'Medium', points: 15 },
      ]
    },
    {
      name: 'Momentum & Energy Test',
      questions: [
        { text: 'Calculate momentum: m=5kg, v=10m/s', topic: 'Momentum', difficulty: 'Easy', points: 10 },
        { text: 'Elastic collision: m1=3kg,v1=5m/s, m2=2kg,v2=0', topic: 'Collisions', difficulty: 'Hard', points: 25 },
        { text: 'Explain conservation of momentum', topic: 'Conceptual', difficulty: 'Easy', points: 10 },
        { text: 'Calculate impulse: F=20N, t=5s', topic: 'Impulse', difficulty: 'Medium', points: 15 },
      ]
    },
    {
      name: 'Energy Conservation Test',
      questions: [
        { text: 'KE of 10kg at 5m/s', topic: 'Energy', difficulty: 'Easy', points: 10 },
        { text: 'Spring PE: k=500N/m, x=0.2m', topic: 'Potential Energy', difficulty: 'Medium', points: 15 },
        { text: 'Work done: F=100N, d=20m, angle=0°', topic: 'Work', difficulty: 'Easy', points: 10 },
        { text: 'Energy conservation: mass drops 10m', topic: 'Conservation', difficulty: 'Hard', points: 25 },
      ]
    }
  ],
  'Chemistry': [
    {
      name: 'Chemical Reactions',
      questions: [
        { text: 'Balance: H₂ + O₂ → H₂O', topic: 'Balancing', difficulty: 'Easy', points: 10 },
        { text: 'Identify type: NaCl + AgNO₃ → NaNO₃ + AgCl', topic: 'Reaction Types', difficulty: 'Medium', points: 15 },
        { text: 'Calculate molar mass of H₂SO₄', topic: 'Moles', difficulty: 'Medium', points: 15 },
        { text: 'Calculate grams from 2 moles of CO₂', topic: 'Stoichiometry', difficulty: 'Hard', points: 20 },
      ]
    },
    {
      name: 'Acids & Bases',
      questions: [
        { text: 'What is pH of solution with [H+] = 1×10⁻⁴', topic: 'pH Calculations', difficulty: 'Medium', points: 15 },
        { text: 'Identify: HCl is acid or base?', topic: 'Acids/Bases', difficulty: 'Easy', points: 10 },
        { text: 'Balance: HCl + NaOH → ?', topic: 'Neutralization', difficulty: 'Medium', points: 15 },
        { text: 'Calculate pH after dilution', topic: 'Dilution', difficulty: 'Hard', points: 20 },
      ]
    }
  ],
  'Biology': [
    {
      name: 'Cell Biology',
      questions: [
        { text: 'Label parts of a cell', topic: 'Cell Structure', difficulty: 'Easy', points: 10 },
        { text: 'Explain mitosis vs meiosis', topic: 'Cell Division', difficulty: 'Medium', points: 15 },
        { text: 'Describe photosynthesis process', topic: 'Photosynthesis', difficulty: 'Hard', points: 20 },
        { text: 'What is ATP function?', topic: 'Energy', difficulty: 'Easy', points: 10 },
      ]
    },
    {
      name: 'Genetics & DNA',
      questions: [
        { text: 'Punnett square: Aa x aa', topic: 'Genetics', difficulty: 'Medium', points: 15 },
        { text: 'What does DNA stand for?', topic: 'Molecular Biology', difficulty: 'Easy', points: 10 },
        { text: 'Explain dominant vs recessive', topic: 'Inheritance', difficulty: 'Medium', points: 15 },
        { text: 'Calculate genotype ratios', topic: 'Probability', difficulty: 'Hard', points: 20 },
      ]
    }
  ],
  'English': [
    {
      name: 'Literature Analysis',
      questions: [
        { text: 'Identify the theme of the passage', topic: 'Theme', difficulty: 'Medium', points: 15 },
        { text: 'What literary device: "The wind whispered"', topic: 'Literary Devices', difficulty: 'Easy', points: 10 },
        { text: 'Analyze character motivation', topic: 'Character Analysis', difficulty: 'Hard', points: 20 },
        { text: 'Write a thesis statement', topic: 'Writing', difficulty: 'Medium', points: 15 },
      ]
    },
    {
      name: 'Grammar & Composition',
      questions: [
        { text: 'Identify subject in sentence', topic: 'Grammar', difficulty: 'Easy', points: 10 },
        { text: 'Correct the sentence fragment', topic: 'Sentence Structure', difficulty: 'Medium', points: 15 },
        { text: 'Use semicolon correctly', topic: 'Punctuation', difficulty: 'Medium', points: 15 },
        { text: 'Write persuasive paragraph', topic: 'Composition', difficulty: 'Hard', points: 20 },
      ]
    }
  ],
  'French': [
    {
      name: 'French Grammar',
      questions: [
        { text: 'Conjugate être in present tense', topic: 'Verbs', difficulty: 'Easy', points: 10 },
        { text: 'Form plural of "le chat"', topic: 'Grammar', difficulty: 'Easy', points: 10 },
        { text: 'Translate: "I am going to the store"', topic: 'Translation', difficulty: 'Medium', points: 15 },
        { text: 'Write paragraph about your day', topic: 'Composition', difficulty: 'Hard', points: 20 },
      ]
    },
    {
      name: 'French Vocabulary',
      questions: [
        { text: 'Name 5 fruits in French', topic: 'Vocabulary', difficulty: 'Easy', points: 10 },
        { text: 'Describe a room using prepositions', topic: 'Prepositions', difficulty: 'Medium', points: 15 },
        { text: 'Use passé composé correctly', topic: 'Past Tense', difficulty: 'Hard', points: 20 },
        { text: 'Dialogue at a restaurant', topic: 'Conversation', difficulty: 'Medium', points: 15 },
      ]
    }
  ],
  'History': [
    {
      name: 'World History',
      questions: [
        { text: 'When did WWI start?', topic: 'Wars', difficulty: 'Easy', points: 10 },
        { text: 'Explain causes of French Revolution', topic: 'Revolutions', difficulty: 'Hard', points: 20 },
        { text: 'Who was Napoleon?', topic: 'Leaders', difficulty: 'Medium', points: 15 },
        { text: 'Timeline of major events 1900-1950', topic: 'Chronology', difficulty: 'Hard', points: 20 },
      ]
    },
    {
      name: 'Ancient Civilizations',
      questions: [
        { text: 'Name 3 Egyptian pharaohs', topic: 'Egypt', difficulty: 'Easy', points: 10 },
        { text: 'Compare Athens and Sparta', topic: 'Greece', difficulty: 'Hard', points: 20 },
        { text: 'What were Roman aqueducts?', topic: 'Rome', difficulty: 'Medium', points: 15 },
        { text: 'Explain Silk Road importance', topic: 'Trade', difficulty: 'Medium', points: 15 },
      ]
    }
  ],
  'Geography': [
    {
      name: 'Physical Geography',
      questions: [
        { text: 'Name the 7 continents', topic: 'Continents', difficulty: 'Easy', points: 10 },
        { text: 'Explain plate tectonics', topic: 'Geology', difficulty: 'Hard', points: 20 },
        { text: 'What causes monsoons?', topic: 'Climate', difficulty: 'Medium', points: 15 },
        { text: 'Label major mountain ranges', topic: 'Topography', difficulty: 'Medium', points: 15 },
      ]
    },
    {
      name: 'Human Geography',
      questions: [
        { text: 'Define urbanization', topic: 'Cities', difficulty: 'Easy', points: 10 },
        { text: 'Explain population pyramids', topic: 'Demographics', difficulty: 'Medium', points: 15 },
        { text: 'Compare rural vs urban', topic: 'Settlement', difficulty: 'Medium', points: 15 },
        { text: 'Analyze migration patterns', topic: 'Migration', difficulty: 'Hard', points: 20 },
      ]
    }
  ],
  'Computer Science': [
    {
      name: 'Programming Basics',
      questions: [
        { text: 'What is a variable?', topic: 'Fundamentals', difficulty: 'Easy', points: 10 },
        { text: 'Write a for loop in Python', topic: 'Loops', difficulty: 'Medium', points: 15 },
        { text: 'Explain if-else statements', topic: 'Conditionals', difficulty: 'Easy', points: 10 },
        { text: 'Debug this code snippet', topic: 'Debugging', difficulty: 'Hard', points: 20 },
      ]
    },
    {
      name: 'Data Structures',
      questions: [
        { text: 'What is an array?', topic: 'Arrays', difficulty: 'Easy', points: 10 },
        { text: 'Implement a stack', topic: 'Stacks', difficulty: 'Hard', points: 20 },
        { text: 'Explain linked lists', topic: 'Lists', difficulty: 'Medium', points: 15 },
        { text: 'Binary search algorithm', topic: 'Algorithms', difficulty: 'Hard', points: 20 },
      ]
    }
  ],
  'Arabic': [
    {
      name: 'Arabic Grammar',
      questions: [
        { text: 'Conjugate verb كتب', topic: 'Verbs', difficulty: 'Medium', points: 15 },
        { text: 'Write your name in Arabic', topic: 'Writing', difficulty: 'Easy', points: 10 },
        { text: 'Explain masculine vs feminine', topic: 'Grammar', difficulty: 'Medium', points: 15 },
        { text: 'Compose paragraph about family', topic: 'Composition', difficulty: 'Hard', points: 20 },
      ]
    },
    {
      name: 'Arabic Literature',
      questions: [
        { text: 'Name a famous Arabic poet', topic: 'Poetry', difficulty: 'Easy', points: 10 },
        { text: 'Analyze a poem excerpt', topic: 'Analysis', difficulty: 'Hard', points: 20 },
        { text: 'Translate passage to English', topic: 'Translation', difficulty: 'Medium', points: 15 },
        { text: 'Explain literary devices', topic: 'Rhetoric', difficulty: 'Medium', points: 15 },
      ]
    }
  ]
};

// Generate tests for all classes
export const TESTS = [];
let testId = 1;

// Get all unique classes for grades 10, 11, 12
const relevantClasses = CLASSES.filter(cls => cls.grade >= 10 && cls.grade <= 12);

relevantClasses.forEach(classObj => {
  const subject = classObj.subject;
  const templates = testTemplates[subject] || [];

  templates.forEach((template, index) => {
    TESTS.push({
      id: testId++,
      name: template.name,
      classId: classObj.id,
      date: `2024-${11 + Math.floor(index / 2)}-${(index % 2) * 15 + 10}`, // Spread dates across Nov-Dec
      questions: template.questions.map((q, qIndex) => ({
        id: qIndex + 1,
        text: q.text,
        topic: q.topic,
        difficulty: q.difficulty,
        points: q.points
      }))
    });
  });
});

// Helper function to generate test score with HIGH variation based on student strengths/weaknesses
function generateTestScore(student, subject, maxScore, difficulty = 1) {
  // Base score from examReadiness
  let basePerformance = student.examReadiness / 100;

  // Apply subject-specific variation based on strengths
  const studentStrengths = student.strengths.map(s => s.toLowerCase());
  const studentWeaknesses = student.weaknesses.map(w => w.toLowerCase());
  const subjectLower = subject.toLowerCase();

  let subjectBonus = 0;
  let subjectPenalty = 0;

  // MUCH STRONGER Strengths boost (up to +35%)
  if (subjectLower.includes('math') && studentStrengths.some(s => s.includes('math') || s.includes('problem') || s.includes('logic'))) {
    subjectBonus = 0.25 + Math.random() * 0.10; // +25% to +35%
  } else if (subjectLower.includes('physic') && studentStrengths.some(s => s.includes('physic') || s.includes('science'))) {
    subjectBonus = 0.25 + Math.random() * 0.10;
  } else if (subjectLower.includes('chemistry') && studentStrengths.some(s => s.includes('chemistry') || s.includes('science'))) {
    subjectBonus = 0.25 + Math.random() * 0.10;
  } else if (subjectLower.includes('biology') && studentStrengths.some(s => s.includes('biology') || s.includes('science'))) {
    subjectBonus = 0.25 + Math.random() * 0.10;
  } else if (subjectLower.includes('english') && studentStrengths.some(s => s.includes('writing') || s.includes('language'))) {
    subjectBonus = 0.25 + Math.random() * 0.10;
  } else if ((subjectLower.includes('french') || subjectLower.includes('arabic')) && studentStrengths.some(s => s.includes('language'))) {
    subjectBonus = 0.20 + Math.random() * 0.10;
  } else if (subjectLower.includes('computer') && studentStrengths.some(s => s.includes('programming') || s.includes('technology') || s.includes('computer'))) {
    subjectBonus = 0.30 + Math.random() * 0.10; // +30% to +40%
  } else if ((subjectLower.includes('history') || subjectLower.includes('geography')) && studentStrengths.some(s => s.includes('writing') || s.includes('memory'))) {
    subjectBonus = 0.20 + Math.random() * 0.10;
  } else {
    // Random small variation for non-strength subjects (-5% to +5%)
    subjectBonus = -0.05 + Math.random() * 0.10;
  }

  // MUCH STRONGER Weaknesses penalty (up to -40%)
  if (subjectLower.includes('math') && studentWeaknesses.some(w => w.includes('math') || w.includes('algebra'))) {
    subjectPenalty = 0.30 + Math.random() * 0.10; // -30% to -40%
  } else if (subjectLower.includes('science') && studentWeaknesses.some(w => w.includes('science'))) {
    subjectPenalty = 0.25 + Math.random() * 0.10;
  } else if ((subjectLower.includes('english') || subjectLower.includes('writing')) && studentWeaknesses.some(w => w.includes('writing'))) {
    subjectPenalty = 0.25 + Math.random() * 0.10;
  } else if ((subjectLower.includes('history') || subjectLower.includes('geography')) && studentWeaknesses.some(w => w.includes('memory'))) {
    subjectPenalty = 0.20 + Math.random() * 0.10;
  } else if (subjectLower.includes('computer') && studentWeaknesses.some(w => w.includes('technology') || w.includes('computer'))) {
    subjectPenalty = 0.25 + Math.random() * 0.10;
  } else {
    // Random small penalty for other subjects (-5% to +5%)
    subjectPenalty = -0.05 + Math.random() * 0.10;
  }

  // Apply modifiers
  basePerformance = basePerformance + subjectBonus - subjectPenalty;

  // Add LARGE test-to-test variation (±25%)
  const testVariation = -0.25 + Math.random() * 0.50;
  basePerformance += testVariation;

  // Apply difficulty multiplier
  const difficultyMultipliers = {
    'Easy': 1.15,
    'Medium': 1.0,
    'Hard': 0.75
  };
  const finalPerformance = basePerformance * (difficultyMultipliers[difficulty] || 1.0);

  // Clamp performance to 0-1 range BEFORE multiplying by maxScore
  const clampedPerformance = Math.max(0.2, Math.min(1.0, finalPerformance));

  // Calculate score - this ensures score never exceeds maxScore
  const score = Math.round(clampedPerformance * maxScore);

  // Ensure score is within valid range
  return Math.max(0, Math.min(maxScore, score));
}

// Helper function to generate detailed question answers
function generateAnswers(student, subject, questions) {
  return questions.map(q => {
    const earnedScore = generateTestScore(student, subject, q.points, q.difficulty);
    const isCorrect = earnedScore >= q.points * 0.8;
    const struggle = earnedScore < q.points * 0.6;

    const issues = [
      'Calculation error',
      'Formula confusion',
      'Incomplete answer',
      'Wrong approach',
      'Conceptual misunderstanding',
      'Sign error',
      'Unit conversion mistake',
      'Missing steps'
    ];

    return {
      questionId: q.id,
      earned: earnedScore,
      max: q.points,
      correct: isCorrect,
      struggle: struggle,
      ...(struggle && { issue: issues[Math.floor(Math.random() * issues.length)] })
    };
  });
}

// Generate comprehensive test results for ALL students in ALL their classes
export const TEST_RESULTS = [];

STUDENTS.forEach(student => {
  // Find all classes this student is enrolled in
  const studentClasses = CLASSES.filter(cls =>
    cls.students && cls.students.includes(student.id)
  );

  // For each class, find all tests and generate results
  studentClasses.forEach(classObj => {
    const classTests = TESTS.filter(test => test.classId === classObj.id);

    classTests.forEach(test => {
      const maxScore = test.questions.reduce((sum, q) => sum + q.points, 0);
      const answers = generateAnswers(student, classObj.subject, test.questions);
      const totalScore = answers.reduce((sum, a) => sum + a.earned, 0);

      // Ensure percentage is between 0 and 100
      const percentage = maxScore > 0 ? Math.min(100, Math.max(0, Math.round((totalScore / maxScore) * 100))) : 0;

      TEST_RESULTS.push({
        studentId: student.id,
        testId: test.id,
        score: totalScore,
        maxScore: maxScore,
        percentage: percentage,
        answers: answers,
        submittedAt: test.date
      });
    });
  });
});

console.log(`Generated ${TESTS.length} tests and ${TEST_RESULTS.length} test results`);
